import { has } from "@olenbetong/appframe-core";

import { Handler } from "mitt";

import { RetrieveParameters } from "./DataHandler.js";
import { DataObject as IDataObject } from "./DataObject.js";
import { StorageArray, StorageRecord } from "./Storage.js";
import { EventTarget, ParameterUpdatedValue } from "./events.js";

type PartialDataLoadedCallbackValue = {
	skip: number;
	count: number;
	total: number;
	refresh?: boolean;
};

type PagingOptions<T extends Record<string, unknown>> = {
	dataObject: IDataObject<T>;
};

export type PagingEvents = {
	beforePageChange: { oldPage: number; newPage: number };
	pageChange: { oldPage: number; newPage: number };
	pageCountChange: { pageCount: number };
	pageRefresh: { page: number };
	beforePageRefresh: { page: number };
};

export type PagingEventsHandlers = { [K in keyof PagingEvents]: CustomEvent<PagingEvents[K]> };

export type PagingEvent = keyof PagingEvents;

const PagingEvents: Record<PagingEvent, { abortable: boolean }> = {
	beforePageChange: { abortable: true },
	beforePageRefresh: { abortable: true },
	pageChange: { abortable: false },
	pageCountChange: { abortable: false },
	pageRefresh: { abortable: false },
};

export class Paging<T extends Record<string, unknown>> extends EventTarget<PagingEvents> {
	private currentData: StorageRecord<T>[] | null = null;
	private currentPage = 0;
	private dataObject: IDataObject<T>;
	private __loadedPages: Record<number, any> = {};
	private options: PagingOptions<T>;
	private pageCount: number;
	private pageSize: number;
	private total: number;

	constructor(pOptions: PagingOptions<T>) {
		super(PagingEvents);
		this.options = { ...pOptions };

		this.dataObject = this.options.dataObject;
		this.pageSize = this.dataObject.getParameter("maxRecords") ?? 50;
		this.total = this.dataObject.getDataLength();
		this.pageCount = this.pageSize === -1 ? 1 : Math.ceil(this.total / this.pageSize);

		this.changePage = this.changePage.bind(this);
		this.getPageCount = this.getPageCount.bind(this);
		this.getCurrentData = this.getCurrentData.bind(this);
		this.getCurrentPage = this.getCurrentPage.bind(this);
		this.loadData = this.loadData.bind(this);
		this.loadedPages = this.loadedPages.bind(this);
		this.refreshPage = this.refreshPage.bind(this);
		this.handlePartialDataLoaded = this.handlePartialDataLoaded.bind(this);
		this.handleParameterUpdated = this.handleParameterUpdated.bind(this);
		this.handleRecordDeleted = this.handleRecordDeleted.bind(this);

		this.attachEvents();

		if (this.dataObject.isDataLoaded()) {
			this.internalChangePage(null, 0);
		}
	}

	private updatePageCount() {
		var pageCount = Math.ceil(this.total / this.pageSize);
		if (this.pageCount === pageCount) {
			return;
		}

		this.pageCount = pageCount;
		this.fireEvent("pageCountChange", { pageCount });
	}

	private internalChangePage(pOldPage: number | null, pNewPage: number) {
		let data: StorageRecord<T>[] = [];
		let startIndex = pNewPage * this.pageSize;
		let stopIndex = startIndex + this.pageSize;

		if (stopIndex > this.total) {
			stopIndex = this.total;
		}
		if (!has(this.__loadedPages, pNewPage)) {
			this.dataObject.retrievePartial(pNewPage * this.pageSize, this.pageSize);
			this.__loadedPages[pNewPage] = false;
			this.currentData = null;
		} else if (this.__loadedPages[pNewPage] === true) {
			for (let i = startIndex; i < stopIndex; i++) {
				data.push(this.dataObject.getData(i));
			}

			this.currentData = data;
		} else {
			this.currentData = null;
		}

		this.currentPage = pNewPage;

		if (pOldPage !== null && this.__loadedPages[pOldPage] === false) {
			delete this.__loadedPages[pOldPage];
		}
	}

	attach<K extends keyof PagingEventsHandlers>(eventName: K, handler: Handler<PagingEventsHandlers[K]>) {
		return this.attachEvent(eventName, handler);
	}

	detach<K extends keyof PagingEventsHandlers>(eventName: K, handler: Handler<PagingEventsHandlers[K]>) {
		return this.detachEvent(eventName, handler);
	}

	async changePage(pPage: number) {
		if (this.dataObject.isDirty()) {
			return;
		}

		if (this.dataObject.getCurrentIndex() === -1) {
			try {
				await this.dataObject.endEdit();
			} catch (_) {
				return;
			}
		}

		if (this.currentPage !== pPage) {
			let args = { oldPage: this.currentPage, newPage: pPage };
			let result = this.fireEvent("beforePageChange", args);

			if (!result) {
				return false;
			}

			this.internalChangePage(args.oldPage, args.newPage);
			this.fireEvent("pageChange", args);
		}

		/*remove new records*/
		if (this.currentPage > 0 && (this.dataObject.isUpdateAllowed() || this.dataObject.isInsertAllowed())) {
			const data = this.dataObject.getData();

			data.forEach((item, index) => {
				if (this.dataObject.isDirty(index) && this.dataObject.isNewRecord(index)) {
					this.dataObject.deleteRow(index);
				}
			});
		}

		let pageSize = this.dataObject.getParameter("maxRecords") ?? 50;
		if (
			this.dataObject.getDataLength() > 0 &&
			Math.floor(this.currentPage * pageSize) !== this.dataObject.getCurrentIndex() &&
			this.dataObject.getCurrentIndex() !== -1
		) {
			this.dataObject.setCurrentIndex(this.currentPage * pageSize);
		}
	}

	refreshPage() {
		let eventData = { page: this.currentPage };
		let result = this.fireEvent("beforePageRefresh", eventData);

		if (!result) {
			return;
		}

		this.currentData = [];

		const startIndex = this.currentPage * this.pageSize;
		let stopIndex = startIndex + this.pageSize;
		if (stopIndex > this.total) {
			stopIndex = this.total;
		}

		for (let i = startIndex; i < stopIndex; i++) {
			this.currentData.push(this.dataObject.getData(i));
		}

		this.fireEvent("pageRefresh", eventData);

		let currentIndex = this.dataObject.getCurrentIndex();
		let pageSize = this.dataObject.getParameter("maxRecords") ?? 50;
		if (this.dataObject.getDataLength() > 0 && currentIndex !== -1 && pageSize >= 0) {
			let firstRecord = this.currentPage * pageSize;
			let nextPage = (this.currentPage + 1) * pageSize;
			if (currentIndex < firstRecord || currentIndex >= nextPage) {
				this.dataObject.setCurrentIndex(this.currentPage * pageSize);
			}
		}
	}

	getPageCount() {
		return this.pageCount;
	}

	getCurrentPage() {
		return this.currentPage;
	}

	getCurrentData(): StorageArray<T> | null {
		return this.currentData;
	}

	private handlePartialDataLoaded(event?: CustomEventInit<PartialDataLoadedCallbackValue>) {
		if (!event) return;

		let data = event.detail;

		if (!data) return;

		const correctSkip = data.skip % this.pageSize === 0;
		const loadedPage = data.skip / this.pageSize;

		if (data.skip === 0) {
			this.total = data.count;
		}

		if (correctSkip) {
			if (data.skip === 0) {
				this.__loadedPages = {};
			}

			this.__loadedPages[loadedPage] = true;

			if (this.currentPage === loadedPage) {
				this.refreshPage();
			}
		} else {
			console.warn("Incorrect skip parameter received from partial data event");
		}
	}

	private handleParameterUpdated<K extends keyof RetrieveParameters<T>>(
		event?: CustomEventInit<ParameterUpdatedValue<T, K>>,
	) {
		if (!event) return;

		let { detail: param } = event;
		let lastPage = 0;
		if (param?.name === "maxRecords") {
			let newPageSize = param["value"] || 25;
			if (this.pageSize !== newPageSize) {
				this.pageSize = newPageSize;

				if (!this.dataObject.isDataLoaded()) {
					return;
				}

				this.updatePageCount();

				if (this.currentPage >= this.total / this.pageSize) {
					if (this.pageSize > 0) {
						lastPage = this.total / this.pageSize;
						if (lastPage > 0) {
							lastPage = Math.ceil(lastPage) - 1;
						}
					}

					this.changePage(lastPage);
				} else {
					this.__loadedPages = {};
					this.internalChangePage(this.currentPage, this.currentPage);
				}
			}
		}
	}

	private attachEvents() {
		this.dataObject.attachEvent("onPartialDataLoaded", this.handlePartialDataLoaded);
		this.dataObject.attachEvent("onParameterUpdated", this.handleParameterUpdated);
		this.dataObject.attachEvent("onRecordDeleted", this.handleRecordDeleted);
		this.dataObject.attachEvent("onRowCountLoaded", this.handleRowCountLoaded);
		this.dataObject.attachEvent("onRecordRefreshed", this.refreshPage);
		this.dataObject.attachEvent("onRecordCreated", () => {
			this.total++;
		});
	}

	private handleRecordDeleted() {
		let prevCount = this.pageCount;
		let lastPage = 0;

		this.total--;
		this.updatePageCount();

		if (this.currentPage >= this.total / this.pageSize) {
			if (this.pageSize > 0) {
				lastPage = this.total / this.pageSize;
				if (lastPage > 0) {
					lastPage = Math.ceil(lastPage) - 1;
				}
			}

			this.refreshPage();
		} else if (this.pageCount !== prevCount || this.currentPage < this.pageCount - 1) {
			if (has(this.__loadedPages, this.currentPage + 1)) {
				this.refreshPage();
			} else {
				delete this.__loadedPages[this.currentPage];
			}
		}
	}

	private handleRowCountLoaded = (event?: CustomEvent<{ count: number; total: number; skip: number }>) => {
		if (!event) return;

		const data = event.detail;
		this.total = data.total;
		this.updatePageCount();

		let correctSkip = data.skip % this.pageSize === 0;
		let loadedPage = data.skip / this.pageSize;

		if (correctSkip) {
			if (data.skip === 0) {
				this.__loadedPages = {};
			}
			this.__loadedPages[loadedPage] = true;
			if (this.currentPage === loadedPage) {
				this.refreshPage();
			} else if (this.currentPage === null && loadedPage === 0) {
				this.changePage(0);
			} else if (this.currentPage > 0 && data.skip === 0) {
				this.changePage(0);
			} else if (data.skip === 0) {
				if (this.currentPage === 0) {
					this.refreshPage();
				} else if (this.currentPage < this.pageCount) {
					this.internalChangePage(null, this.currentPage);
				} else {
					this.changePage(Math.max(0, this.pageCount - 1));
				}
			}
		} else {
			console.warn("Incorrect skip parameter received from partial data event");
		}
	};

	loadData(forceLoad: boolean) {
		if (this.dataObject.isDataLoaded() && forceLoad === false) {
			this.internalChangePage(null, 0);
		} else if (!this.dataObject.isDataLoading() || forceLoad === true) {
			this.dataObject.retrievePartial(0, this.pageSize);
		}
	}

	loadedPages() {
		return this.__loadedPages;
	}
}
